import React, { useEffect, useState } from 'react';
import secureStorage from '../utils/secureStorage';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { Camera, Edit, Link2, Play, Plus, Trash2, Upload, Video, X } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const API = `${API_BASE_URL}/api`;

const TutorialsView = () => {
  const API = API_BASE_URL + '/api';
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTutorial, setEditingTutorial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    video_type: 'link',
    video_url: '',
    video_data: null,
    video_filename: '',
    thumbnail: null
  });
  const [uploading, setUploading] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    fetchTutorials();
  }, []);

  const fetchTutorials = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/tutorials`, { headers });
      setTutorials(response.data);
    } catch (error) {
      console.error('Error fetching tutorials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 500 * 1024 * 1024) { // 500MB limit
        alert('Video file size must be less than 500MB');
        return;
      }
      
      setUploading(true);
      
      try {
        // Upload file to server
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        
        const token = secureStorage.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const response = await axios.post(`${API}/tutorials/upload-video`, uploadFormData, {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload progress: ${percentCompleted}%`);
          }
        });
        
        if (response.data.success) {
          setFormData(prev => ({
            ...prev,
            video_url: response.data.video_url,  // Use server URL instead of base64
            video_data: null,  // Clear any base64 data
            video_filename: response.data.filename,
            video_type: 'upload'
          }));
          alert('Video uploaded successfully!');
        } else {
          throw new Error(response.data.message || 'Upload failed');
        }
      } catch (error) {
        console.error('Error uploading video:', error);
        alert(`Error uploading video: ${error.response?.data?.detail || error.message}`);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleThumbnailUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({ ...prev, thumbnail: e.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Please enter a tutorial name');
      return;
    }
    if (formData.video_type === 'link' && !formData.video_url?.trim()) {
      alert('Please enter a video URL');
      return;
    }
    // For uploads, check if video_url is set (from successful file upload)
    if (formData.video_type === 'upload' && !formData.video_url) {
      alert('Please upload a video file first');
      return;
    }

    try {
      const token = secureStorage.getToken();
      const headers = { 
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };
      
      // Prepare data for submission - only include necessary fields
      const submitData = {
        name: formData.name.trim(),
        description: formData.description || '',
        video_type: formData.video_type || 'link',
        video_url: formData.video_url || null,
        video_filename: formData.video_filename || null,
        thumbnail: null  // Thumbnails handled separately
      };
      
      console.log('Submitting tutorial:', submitData);
      
      if (editingTutorial) {
        await axios.put(`${API}/tutorials/${editingTutorial.id}`, submitData, { headers, timeout: 30000 });
      } else {
        await axios.post(`${API}/tutorials`, submitData, { headers, timeout: 30000 });
      }
      
      fetchTutorials();
      resetForm();
    } catch (error) {
      console.error('Error saving tutorial:', error);
      const errorMsg = error.response?.data?.detail || error.response?.statusText || error.message;
      alert(`Error saving tutorial: ${errorMsg}`);
    }
  };

  const handleEdit = (tutorial) => {
    setEditingTutorial(tutorial);
    setFormData({
      name: tutorial.name || '',
      description: tutorial.description || '',
      video_type: tutorial.video_type || 'link',
      video_url: tutorial.video_url || '',
      video_data: tutorial.video_data || null,
      video_filename: tutorial.video_filename || '',
      thumbnail: tutorial.thumbnail || null
    });
    setShowForm(true);
  };

  const handleDelete = async (tutorialId) => {
    if (!window.confirm('Are you sure you want to delete this tutorial?')) return;
    
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/tutorials/${tutorialId}`, { headers });
      fetchTutorials();
    } catch (error) {
      console.error('Error deleting tutorial:', error);
      alert('Error deleting tutorial. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      video_type: 'link',
      video_url: '',
      video_data: null,
      video_filename: '',
      thumbnail: null
    });
    setEditingTutorial(null);
    setShowForm(false);
  };

  const getEmbedUrl = (url) => {
    if (!url) return null;
    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (youtubeMatch) return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    // Loom - convert share links to embed links
    const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
    if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
    return url;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Tutorials</h1>
          <p className="text-gray-400">Create and manage training tutorials</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Tutorial
        </Button>
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-gray-800 border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="text-orange-500">
                {editingTutorial ? 'Edit Tutorial' : 'Add New Tutorial'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-gray-300">Tutorial Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter tutorial name"
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what this tutorial covers..."
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Video Source</Label>
                  <div className="flex space-x-4 mt-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="video_type"
                        value="link"
                        checked={formData.video_type === 'link'}
                        onChange={() => setFormData(prev => ({ ...prev, video_type: 'link', video_data: null, video_filename: '' }))}
                        className="text-orange-500"
                      />
                      <span className="text-gray-300">Video Link (Loom/YouTube/Vimeo)</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="video_type"
                        value="upload"
                        checked={formData.video_type === 'upload'}
                        onChange={() => setFormData(prev => ({ ...prev, video_type: 'upload', video_url: '' }))}
                        className="text-orange-500"
                      />
                      <span className="text-gray-300">Upload Video</span>
                    </label>
                  </div>
                </div>

                {formData.video_type === 'link' ? (
                  <div>
                    <Label className="text-gray-300">Video URL *</Label>
                    <Input
                      value={formData.video_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                      placeholder="https://www.loom.com/share/... or https://youtube.com/..."
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                    <p className="text-xs text-gray-400 mt-1">Supports YouTube, Vimeo, and Loom links</p>
                  </div>
                ) : (
                  <div>
                    <Label className="text-gray-300">Upload Video *</Label>
                    <div className="mt-2">
                      <input
                        type="file"
                        accept="video/*"
                        onChange={handleVideoUpload}
                        className="hidden"
                        id="video-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('video-upload').click()}
                        disabled={uploading}
                        className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                      >
                        {uploading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500 mr-2"></div>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Choose Video File
                          </>
                        )}
                      </Button>
                      {formData.video_filename && (
                        <p className="text-sm text-green-400 mt-2">
                          ✓ {formData.video_filename}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">Max file size: 500MB</p>
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-gray-300">Thumbnail Image (Optional)</Label>
                  <div className="mt-2 flex items-center space-x-4">
                    {formData.thumbnail && (
                      <img src={formData.thumbnail} alt="Thumbnail" className="w-24 h-16 object-cover rounded" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailUpload}
                      className="hidden"
                      id="thumbnail-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('thumbnail-upload').click()}
                      className="border-gray-500/50 text-gray-300 hover:bg-gray-700"
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      {formData.thumbnail ? 'Change' : 'Add'} Thumbnail
                    </Button>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm} className="border-gray-500 text-gray-300">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-black font-semibold">
                    {editingTutorial ? 'Update Tutorial' : 'Add Tutorial'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Video Player Modal */}
      {playingVideo && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setPlayingVideo(null)}>
          <div className="relative w-full max-w-4xl" onClick={e => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPlayingVideo(null)}
              className="absolute -top-10 right-0 text-white hover:text-orange-500"
            >
              <X className="h-6 w-6" />
            </Button>
            {playingVideo.video_type === 'link' ? (
              <iframe
                src={getEmbedUrl(playingVideo.video_url)}
                className="w-full aspect-video rounded-lg"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            ) : (
              <video
                src={playingVideo.video_url?.startsWith('/uploads') ? `${API_BASE_URL}${playingVideo.video_url}` : (playingVideo.video_data || playingVideo.video_url)}
                className="w-full rounded-lg"
                controls
                autoPlay
              />
            )}
            <h3 className="text-white text-xl font-semibold mt-4">{playingVideo.name}</h3>
            <p className="text-gray-400 mt-2">{playingVideo.description}</p>
          </div>
        </div>
      )}

      {/* Tutorials List */}
      {tutorials.length === 0 ? (
        <Card className="bg-gray-800 border-gray-700 border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Video className="h-12 w-12 text-gray-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">No Tutorials Yet</h3>
            <p className="text-gray-500 text-center mb-4">
              Create training tutorials to help your team prepare for exercises.
            </p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Tutorial
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tutorials.map((tutorial) => (
            <Card key={tutorial.id} className="bg-gray-800 border-gray-700 hover:border-orange-500/50 transition-colors overflow-hidden">
              <div 
                className="relative h-40 bg-gray-700 cursor-pointer group"
                onClick={() => setPlayingVideo(tutorial)}
              >
                {tutorial.thumbnail ? (
                  <img src={tutorial.thumbnail} alt={tutorial.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Video className="h-16 w-16 text-gray-500" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Play className="h-16 w-16 text-white" />
                </div>
                <Badge className="absolute top-2 right-2 bg-gray-900/80 text-gray-300">
                  {tutorial.video_type === 'link' ? (
                    <><Link2 className="h-3 w-3 mr-1" /> Link</>
                  ) : (
                    <><Video className="h-3 w-3 mr-1" /> Uploaded</>
                  )}
                </Badge>
              </div>
              <CardContent className="p-4">
                <h3 className="text-lg font-semibold text-white mb-2">{tutorial.name}</h3>
                <p className="text-gray-400 text-sm line-clamp-2 mb-4">{tutorial.description || 'No description'}</p>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">
                    {tutorial.created_at ? new Date(tutorial.created_at).toLocaleDateString() : ''}
                  </span>
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(tutorial)}
                      className="text-blue-400 hover:text-blue-300"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(tutorial.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ICS 218 - Support Vehicle / Equipment Inventory Form

export default TutorialsView;
